import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Dialog } from "../components/Dialog.js";
import { describeApiError } from "../domain/errors.js";
import type { DeploymentRole, DesktopBridge, DesktopSettings, DesktopSettingsPatch } from "./bridge.js";
import { DEFAULT_LAN_PORT } from "./bridge.js";
import { EmployeeManager } from "./EmployeeManager.js";
import { LegacyImport } from "./LegacyImport.js";
import { PrinterSelect } from "./PrinterSelect.js";
import "./desktop.css";

/**
 * The desktop settings screen — the successor to legacy's `Settings.xaml`,
 * reached from the top bar (`AppShell`, desktop only). Edits everything the
 * setup wizard collected: role, printer, LAN serving / connection, and the
 * employees. It is deliberately the same building blocks as the wizard
 * (`PrinterSelect`, `EmployeeManager`) so the two never drift.
 *
 * Saving persists in one patch; the shell reacts (`main.ts`) — arming the LAN,
 * or switching this install to a client that connects elsewhere, restarts the
 * relevant part live.
 */
export function SettingsModal({
  bridge,
  settings,
  persist,
  onClose,
}: {
  bridge: DesktopBridge;
  settings: DesktopSettings;
  /** Persists a patch (and keeps the cached settings in sync). */
  persist: (patch: DesktopSettingsPatch) => Promise<DesktopSettings>;
  onClose: () => void;
}) {
  const [role, setRole] = useState<DeploymentRole>(settings.role);
  const [printerName, setPrinterName] = useState<string | null>(settings.printerName);
  const [lanEnabled, setLanEnabled] = useState(settings.lan.enabled);
  const [lanPassphrase, setLanPassphrase] = useState(settings.lan.passphrase ?? "");
  const [remoteHost, setRemoteHost] = useState(settings.remote.host ?? "");
  const [remotePort, setRemotePort] = useState(settings.remote.port);
  const [remotePassphrase, setRemotePassphrase] = useState(settings.remote.passphrase ?? "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only meaningful for a host: the addresses this machine answers on. A client has no embedded server, so never ask.
  const statusQuery = useQuery({
    queryKey: ["desktop-server-status"],
    queryFn: () => bridge.server.status(),
    enabled: role === "host",
  });

  const lanBlocked = role === "host" && lanEnabled && lanPassphrase.trim().length < 4;
  const connectBlocked = role === "client" && (remoteHost.trim().length === 0 || remotePassphrase.trim().length === 0);
  const saveBlocked = lanBlocked || connectBlocked || saving;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await persist({
        role,
        printerName,
        ...(role === "host"
          ? { lan: { enabled: lanEnabled, passphrase: lanEnabled ? lanPassphrase.trim() : null } }
          : { remote: { host: remoteHost.trim(), port: remotePort, passphrase: remotePassphrase.trim() } }),
      });
      onClose();
    } catch (err) {
      setError(describeApiError(err));
      setSaving(false);
    }
  }

  return (
    <Dialog title="Nastavitve" size="wide" onClose={onClose}>
      <div className="settings-body">
        <section className="settings-section">
          <h3>Vloga</h3>
          <label className="settings-radio">
            <input type="radio" name="settings-role" checked={role === "host"} onChange={() => setRole("host")} />
            <span>Glavni računalnik (hrani podatke)</span>
          </label>
          <label className="settings-radio">
            <input type="radio" name="settings-role" checked={role === "client"} onChange={() => setRole("client")} />
            <span>Omrežni odjemalec (poveže se na glavni računalnik)</span>
          </label>
          {role !== settings.role && <p className="setup-note">Sprememba vloge se uveljavi po shranjevanju — okno se bo ponovno naložilo.</p>}
        </section>

        <section className="settings-section">
          <h3>Tiskalnik</h3>
          <label htmlFor="settings-printer">Tiskalnik za potrdila</label>
          <PrinterSelect bridge={bridge} value={printerName} onChange={setPrinterName} id="settings-printer" />
        </section>

        {role === "host" ? (
          <section className="settings-section">
            <h3>Omrežni dostop</h3>
            <label className="settings-toggle">
              <input type="checkbox" checked={lanEnabled} onChange={(e) => setLanEnabled(e.target.checked)} />
              <span>Streži tudi v omrežju</span>
            </label>
            {lanEnabled && (
              <>
                <label htmlFor="settings-lan-pass">Skupno geslo za omrežje</label>
                <input id="settings-lan-pass" type="password" value={lanPassphrase} onChange={(e) => setLanPassphrase(e.target.value)} autoComplete="new-password" />
                {lanBlocked && <p className="settings-error">Geslo mora imeti vsaj 4 znake.</p>}
                {statusQuery.data && statusQuery.data.lanUrls.length > 0 && (
                  <p className="setup-note">
                    Dosegljivo v omrežju na: {statusQuery.data.lanUrls.join(", ")}
                  </p>
                )}
              </>
            )}
          </section>
        ) : (
          <section className="settings-section">
            <h3>Povezava na glavni računalnik</h3>
            <label htmlFor="settings-remote-host">Naslov (IP)</label>
            <input id="settings-remote-host" value={remoteHost} onChange={(e) => setRemoteHost(e.target.value)} placeholder="192.168.1.10" autoComplete="off" />
            <label htmlFor="settings-remote-port">Vrata</label>
            <input id="settings-remote-port" type="number" value={remotePort} onChange={(e) => setRemotePort(Number(e.target.value) || DEFAULT_LAN_PORT)} min={1} max={65535} />
            <label htmlFor="settings-remote-pass">Skupno geslo</label>
            <input id="settings-remote-pass" type="password" value={remotePassphrase} onChange={(e) => setRemotePassphrase(e.target.value)} autoComplete="current-password" />
          </section>
        )}

        {role === "host" && (
          <section className="settings-section">
            <h3>Zaposleni</h3>
            <EmployeeManager />
          </section>
        )}

        {role === "host" && (
          <section className="settings-section">
            <h3>Uvoz stare baze (MongoDB)</h3>
            <LegacyImport bridge={bridge} />
          </section>
        )}

        {error && (
          <p className="settings-error" role="alert">
            {error}
          </p>
        )}

        <footer className="settings-footer">
          <button type="button" className="settings-cancel" onClick={onClose} disabled={saving}>
            Prekliči
          </button>
          <button type="button" className="settings-save" onClick={() => void save()} disabled={saveBlocked}>
            {saving ? "Shranjevanje …" : "Shrani"}
          </button>
        </footer>
      </div>
    </Dialog>
  );
}
